import { useState } from "react";
import JsonLd from "@/components/base/JsonLd";
import { faqs } from "@/mocks/melonData";
import { categories } from "@/mocks/melonData";

export function FaqSection() {
  const [openIdx, setOpenIdx] = useState<number>(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <JsonLd data={faqSchema} />
      <section id="faq" className="bg-background-100/70">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-500">
              Quick Guide
            </p>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
              吃瓜指南 · 常见问题
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              关于实锤如何认定、名单如何分级、信息如何更新，看完这里你会更清楚怎么用。
            </p>
          </div>

          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => {
              const open = openIdx === i;
              return (
                <div
                  key={f.q}
                  className={`overflow-hidden rounded-xl border bg-background-50 transition-colors ${
                    open ? "border-primary-200" : "border-background-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                        {i + 1}
                      </span>
                      <span className="text-sm font-semibold text-foreground-900 md:text-base">
                        {f.q}
                      </span>
                    </span>
                    <i
                      className={`text-xl text-foreground-500 transition-transform ${open ? "ri-subtract-line" : "ri-add-line"}`}
                    ></i>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pl-14">
                      <p className="text-sm leading-relaxed text-foreground-600">{f.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

const principles = [
  { icon: "ri-search-eye-line", title: "先核实后收录", desc: "单条爆料不直接上名单，交叉核对公开说明、官方口径与多方信源后才收录。" },
  { icon: "ri-file-shield-2-line", title: "逐条标注信源", desc: "每条收录都附信源字段，你能看到结论来自何处，也方便自己复查。" },
  { icon: "ri-list-check-3", title: "区分定论等级", desc: "确认瓜、落地瓜与盖棺定论清晰分级，不把猜测写成事实。" },
  { icon: "ri-refresh-line", title: "及时跟进反转", desc: "吃瓜常会反转，一旦有新信息，我们会更新原条目状态并标注更新时间。" },
];

const reviews = [
  { text: "以前刷到爆料总是一头雾水，这里把「有没有定论」分得很清楚，省了我很多来回对比的时间。", from: "编辑部读者 · 追剧人" },
  { text: "标了信源这点很加分，信息能自己顺着往回查，比只丢结论的账号可信得多。", from: "栏目关注者 · 数码区" },
];

export function AboutSection() {
  return (
    <section id="about" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr] lg:gap-14">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
            About Us
          </p>
          <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
            关于吃瓜今天
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            吃瓜今天是一档专注于<span className="font-semibold text-primary-700">娱乐热点聚合与整理</span>的每日栏目。我们不做爆料的第一现场，而是当「瓜落定之后的整理员」——把分散在各处的公开回应、官方口径与多方信源放到一起交叉核对，再把真正达成共识的结论，以石锤合集和实锤名单的形式呈现给你。
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            我们的定位是「有据可循的吃瓜」。同一件事，我们关心的不是流量，而是它是否已经<strong>可以交叉印证</strong>。先核实后收录，标注信源等级，区分传闻、辟谣、实锤与落地，并在反转出现时及时更新——让每一口瓜都经得起回头看。
          </p>

          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.title}
                className="rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-200"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                  <i className={p.icon}></i>
                </span>
                <h3 className="mt-3 text-sm font-semibold text-foreground-900">{p.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 rounded-lg border border-background-200 bg-background-100/70 px-4 py-3 text-xs leading-relaxed text-foreground-600">
            <i className="ri-information-line mr-1 text-accent-600"></i>
            说明：本站为娱乐热点聚合整理栏目。收录内容均以多方可交叉印证的公开信息为准，名单中的「实锤」仅表示多方口径一致的公开结论，不代表对任何当事人作出定性评价；若后续出现新信息，条目会及时更新并标注时间。
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
              <i className="ri-community-line text-primary-500"></i>
              读者这样说
            </h3>
            <div className="mt-4 space-y-3">
              {reviews.map((r) => (
                <figure
                  key={r.from}
                  className="rounded-lg bg-background-100 p-4"
                >
                  <blockquote className="text-sm leading-relaxed text-foreground-700">
                    「{r.text}」
                  </blockquote>
                  <figcaption className="mt-2 flex items-center gap-1.5 text-xs font-medium text-foreground-500">
                    <i className="ri-vip-crown-line text-secondary-600"></i>
                    {r.from}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-primary-500 p-5 text-background-50">
            <h3 className="flex items-center gap-2 font-heading text-lg">
              <i className="ri-mail-send-line"></i>
              联系我们
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-primary-100">
              发现我们漏掉的关键定论，或想反馈内容条目，欢迎来信。
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <a
                href="mailto:hi@chigua-today.example"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50/15 px-3.5 py-1.5 font-medium transition-colors hover:bg-background-50/25"
              >
                <i className="ri-mail-line"></i>
                hi@chigua-today.example
              </a>
              <p className="flex items-center gap-2 text-primary-100">
                <i className="ri-time-line"></i>
                工作日 09:00 – 21:00 回复
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const quickNav = [
  { id: "today", label: "今日实锤" },
  { id: "list", label: "实锤名单" },
  { id: "topics", label: "热门栏目" },
  { id: "hot", label: "本周热榜" },
  { id: "faq", label: "吃瓜指南" },
  { id: "about", label: "关于我们" },
];

export function SiteFooter() {
  return (
    <footer className="bg-primary-700 text-primary-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 text-xl">
                🍉
              </span>
              <span className="font-heading text-xl tracking-wide text-background-50">
                吃瓜今天
              </span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-primary-100">
              每日更新已实锤的石锤合集与实锤名单，先核实后收录，逐条标注信源，帮你快速分辨哪些娱乐热点真正有定论。
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50">快速导航</h4>
            <ul className="mt-4 space-y-2.5">
              {quickNav.map((q) => (
                <li key={q.id}>
                  <a
                    href={`#${q.id}`}
                    className="text-sm text-primary-100 transition-colors hover:text-background-50"
                  >
                    {q.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50">常驻栏目</h4>
            <ul className="mt-4 space-y-2.5">
              {categories.map((c) => (
                <li key={c.id}>
                  <a
                    href="#list"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-100 transition-colors hover:text-background-50"
                  >
                    <i className={c.icon}></i>
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-primary-50/20 pt-6 sm:flex-row">
          <p className="text-xs text-primary-100">
            © {new Date().getFullYear()} 吃瓜今天 · 娱乐热点聚合与整理栏目
          </p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-primary-100">
            <a href="#about" className="transition-colors hover:text-background-50">
              内容原则
            </a>
            <a
              href="mailto:hi@chigua-today.example"
              className="inline-flex items-center gap-1 transition-colors hover:text-background-50"
            >
              <i className="ri-mail-line"></i>
              联系我们
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}